import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';

import SectionTitles from './components/SectionTitles.jsx';
import SubscriptionWidget from './components/SubscriptionWidget.jsx';
import InitPage from './components/InitPage.jsx';
import DetailsStep from './components/DetailsStep.jsx';
import ConfigurationStep from './components/ConfigurationStep.jsx';
import SummaryStep from './components/SummaryStep.jsx';
import ThankYouStep from './components/ThankYouStep.jsx';

import { useSubscriptionProducts } from './hooks/useSubscriptionProducts';
import { useSubscriptionDetails } from './hooks/useSubscriptionDetails';
import { useWeightGroupSelection } from './hooks/useWeightGroupSelection';
import { usePersistDepositedData } from './hooks/usePersistDepositedData';
import { useStepNavigation } from './hooks/useStepNavigation';

/**
 * Formats amount using window.store.currencyFormat (e.g. '€{{amount_with_comma_separator}} EUR').
 * @param {number} amount - Price in main unit (e.g. 12.34 for dollars)
 * @returns {string}
 */
function formatMoney(amount) {
	let format = (typeof window !== 'undefined' && window.store?.currencyFormat) || '{{amount}}';
	format = format.replace('<span class=money>', '').replace('</span>', '');
	const match = format.match(/\{\{([^}]+)\}\}/);
	if (!match) return String(amount);
	const placeholder = match[1];
	const decimals = Number(amount).toFixed(2);
	const parts = decimals.split('.');
	const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
	const decPart = parts[1];
	let formattedAmount;
	if (placeholder === 'amount_with_comma_separator') {
		formattedAmount = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + decPart;
	} else if (placeholder === 'amount_no_decimals') {
		formattedAmount = Math.round(amount).toLocaleString('en-US', { maximumFractionDigits: 0 });
	} else if (placeholder === 'amount_with_comma_separator_and_no_decimals') {

		formattedAmount = Math.round(amount).replace
			.toString()
			.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
	} else {
		formattedAmount = intPart + '.' + decPart;
	}
	return format.replace(match[0], formattedAmount);
}

const INITIAL_DEPOSITED_ITEM = {
	dogWeight: 0,
	dogWeightUnit: 'lbs',
	products: [],
	subscription: {},
	discount: 0,
	subscription_total_price: 0,
};

function App({ sectionsAlreadyOnThePage, pagePlaceholder, thankYouPageHandle, tableType, subscriptionDisplay, titleObject, logoUrl }) {
	const [step, setStep] = useState(1);
	const [subscriptionProducts, setSubscriptionProducts] = useState(false);
	const [subscriptionsDetails, setSubscriptionsDetails] = useState([]);
	const [depositedData, setDepositedData] = useState([{ ...INITIAL_DEPOSITED_ITEM }]);
	const [currentDog, setCurrentDog] = useState(0);
	const [widgetError, setWidgetError] = useState('');
	const [listProductHandles, setListProductHandles] = useState([]);
	const [consentIsClicked, setConsentIsClicked] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();

	useSubscriptionProducts({
		setSubscriptionProducts,
		setListProductHandles,
		setDepositedData,
		subscriptionProducts,
		currentDog,
	});

	useSubscriptionDetails(subscriptionsDetails, setSubscriptionsDetails);

	useWeightGroupSelection(subscriptionsDetails, setSubscriptionsDetails, depositedData, currentDog);

	usePersistDepositedData(depositedData, step);

	useStepNavigation(step, location, navigate, setStep);

	
	

	const findSubscriptionAndAddToCart = async () => {
		const configObjectForCart = depositedData.map((subscription) => {
			const randomNumberBatch = Math.floor(Math.random() * 1000000000);
			return subscription.products.map((product) => {
				const lineItemObject = {
					handle: product,
					variantId: subscriptionProducts[product].fetchData.variants[0].id,
					quantity: subscription.subscription.products[product].custom_bottles,
				};

				const productSubscriptions = subscriptionProducts[product].fetchData.selling_plan_groups;
				const prodSub = productSubscriptions
					.map((el) => {
						return el.selling_plans.find((plan) => {
							const planDays = parseInt(plan.name.match(/\d+/)[0]);
							return planDays === subscription.subscription.days && plan.price_adjustments[0].value === subscription.discount;
						});
					})
					.filter((el) => el !== undefined);

				lineItemObject.selling_plan = prodSub[0];
				lineItemObject.properties = {
					Subscription: prodSub[0] ? prodSub[0].name : '',
					'Ref Code': randomNumberBatch,
					_selected_selling_plan_ids: JSON.stringify([prodSub[0] ? prodSub[0].id : '']),
				};

				return lineItemObject;
			});
		});

		for (const [_, config] of configObjectForCart.entries()) {
			const formData = {
				items: config.map((item) => ({
					id: item.variantId,
					quantity: item.quantity,
					...(item.selling_plan && { selling_plan: item.selling_plan.id }),
					properties: item.properties,
				})),
			};

			await fetch('/cart/add.js', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(formData),
			});
		}

		const cartDrawer = document.getElementById('Cart-Drawer');
		const sectionsResponse = await fetch('/cart/update.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				items: [],
				sections: 'cart-drawer,cart-bubble',
				sections_url: window.location.pathname,
			}),
		});
		const sectionsText = await sectionsResponse.text();

		if (window.CartDrawer) {
			window.CartDrawer.updateCartUI(sectionsText);
		}

		if (cartDrawer) {
			document.body.classList.add('open-cc');
			cartDrawer.classList.add('active');
			cartDrawer.focus();
		}
	};

	const setDepositedDataReset = (handles) => {
		setDepositedData([
			{
				...INITIAL_DEPOSITED_ITEM,
				products: [...handles],
				discount: handles.length * 5 > 20 ? 20 : handles.length * 5,
			},
		]);
	};

	return (
		<>
			{subscriptionProducts ? (
				<>
					<SubscriptionWidget widgetError={widgetError} currentDog={currentDog} step={step} depositedData={depositedData} />
					<SectionTitles step={step} titleObject={titleObject} logoUrl={logoUrl} />
					<Routes>
						<Route
							path="/pages/subscriptions"
							element={<InitPage sectionsAlreadyOnThePage={sectionsAlreadyOnThePage} step={step} setStep={setStep} />}
						/>
						<Route
							path="/pages/subscriptions/details"
							element={
								<DetailsStep
									depositedData={depositedData}
									setDepositedData={setDepositedData}
									currentDog={currentDog}
									subscriptionProducts={subscriptionProducts}
									step={step}
									setStep={setStep}
									setWidgetError={setWidgetError}
									navigate={navigate}
								/>
							}
						/>
						<Route
							path="/pages/subscriptions/configuration"
							element={
								<ConfigurationStep
									subscriptionProducts={subscriptionProducts}
									subscriptionsDetails={subscriptionsDetails}
									depositedData={depositedData}
									setDepositedData={setDepositedData}
									currentDog={currentDog}
									subscriptionDisplay={subscriptionDisplay}
									setSubscriptionsDetails={setSubscriptionsDetails}
									step={step}
									setStep={setStep}
									setWidgetError={setWidgetError}
									navigate={navigate}
									formatMoney={formatMoney}
								/>
							}
						/>
						<Route
							path="/pages/subscriptions/summary"
							element={
								<SummaryStep
									subscriptionProducts={subscriptionProducts}
									subscriptionsDetails={subscriptionsDetails}
									depositedData={depositedData}
									setDepositedData={setDepositedData}
									currentDog={currentDog}
									step={step}
									setStep={setStep}
									setConsentIsClicked={setConsentIsClicked}
									consentIsClicked={consentIsClicked}
									tableType={tableType}
									sectionsAlreadyOnThePage={sectionsAlreadyOnThePage}
									listProductHandles={listProductHandles}
									setDepositedDataReset={setDepositedDataReset}
									thankYouPageHandle={thankYouPageHandle}
									navigate={navigate}
									findSubscriptionAndAddToCart={findSubscriptionAndAddToCart}
									setWidgetError={setWidgetError}
									formatMoney={formatMoney}
								/>
							}
						/>
						<Route path="/pages/subscriptions/thank-you" element={<ThankYouStep sectionsAlreadyOnThePage={sectionsAlreadyOnThePage} />} />
					</Routes>
				</>
			) : (
				<div className="row small-12">
					<div
						className="subscription-configurator__placeholder flex-container flex-dir-column align-center-middle column small-12 section-spacing-padding-mobile"
						dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(pagePlaceholder) }}
					/>
				</div>
			)}
		</>
	);
}

export default App;
